/**
 * Reputation Stake Dashboard - Quality enforcement, tier visualization, event history
 * Hotels commit tokens as reputation stake. Bad behavior triggers slashing; good scores maintain standing.
 */
import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import {
  ArrowLeft, AlertTriangle, CheckCircle2, Coins,
  Flame, Gauge, Lock, RefreshCw, Search,
  Shield, ShieldAlert, Sparkles, TrendingDown,
  TrendingUp, Zap,
} from 'lucide-react';
import {
  getStakeStatus, evaluateStaking, getSlashHistory, getRewardHistory,
} from '@/lib/v2Api';
import TerminalFloorNav from '../components/TerminalFloorNav';

const fmt = (n) => new Intl.NumberFormat().format(n || 0);

const TIERS = [
  { name: 'excellent', min: 900, max: 1000, slash: 0, reward: 'full', color: 'bg-emerald-500', text: 'text-emerald-500', badge: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30' },
  { name: 'great', min: 800, max: 899, slash: 0, reward: 'partial', color: 'bg-teal-500', text: 'text-teal-500', badge: 'bg-teal-500/15 text-teal-500 border-teal-500/30' },
  { name: 'good', min: 700, max: 799, slash: 0, reward: 'eligible', color: 'bg-blue-500', text: 'text-blue-500', badge: 'bg-blue-500/15 text-blue-500 border-blue-500/30' },
  { name: 'warning', min: 500, max: 699, slash: 0, reward: 'restricted', color: 'bg-amber-500', text: 'text-amber-500', badge: 'bg-amber-500/15 text-amber-500 border-amber-500/30' },
  { name: 'poor', min: 300, max: 499, slash: 5, reward: 'restricted', color: 'bg-orange-500', text: 'text-orange-500', badge: 'bg-orange-500/15 text-orange-500 border-orange-500/30' },
  { name: 'unacceptable', min: 0, max: 299, slash: 10, reward: 'restricted', color: 'bg-red-500', text: 'text-red-500', badge: 'bg-red-500/15 text-red-500 border-red-500/30' },
];

const getTier = (score) => TIERS.find(t => score >= t.min && score <= t.max) || TIERS[5];

// ─── Stake Overview ───
const StakeOverview = ({ stake }) => {
  const tier = getTier(stake.current_score);
  const healthPct = (stake.current_score / 1000) * 100;
  const slashPct = stake.original_stake > 0 ? ((stake.total_slashed / stake.original_stake) * 100).toFixed(1) : 0;

  return (
    <div className="space-y-5" data-testid="stake-overview">
      {/* Main KPIs */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { title: 'Reputation Stake', value: fmt(stake.staked_amount), icon: Lock, color: 'text-[hsl(var(--web3-primary))]', sub: `Original: ${fmt(stake.original_stake)}` },
          { title: 'Total Slashed', value: fmt(stake.total_slashed), icon: Flame, color: 'text-red-500', sub: `${slashPct}% of original` },
          { title: 'Tokens Distributed', value: fmt(stake.total_rewards_earned), icon: Sparkles, color: 'text-emerald-500', sub: stake.rewards_frozen ? 'Currently restricted' : 'Eligible for allocation' },
          { title: 'Quality Score', value: stake.current_score.toFixed(0), icon: Gauge, color: tier.text, sub: `Tier: ${tier.name}` },
        ].map(({ title, value, icon: Icon, color, sub }) => (
          <Card key={title} className="card-hover-lift" data-testid={`staking-kpi-${title.toLowerCase().replace(/ /g, '-')}`}>
            <CardContent className="pt-5 pb-4 px-5">
              <div className="flex items-start justify-between">
                <div className="space-y-1.5">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">{title}</p>
                  <p className="text-2xl md:text-3xl heading-font tabular-nums font-semibold">{value}</p>
                  <p className="text-xs text-muted-foreground">{sub}</p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-[hsl(var(--web3-surface-2))]/80 flex items-center justify-center shrink-0">
                  <Icon className={`h-5 w-5 ${color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tier Gauge */}
      <Card data-testid="tier-gauge-card">
        <CardContent className="pt-5 pb-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Health Score</p>
              <div className="flex items-center gap-2 mt-1">
                <span className={`text-3xl heading-font font-bold tabular-nums ${tier.text}`}>{stake.current_score.toFixed(0)}</span>
                <span className="text-sm text-muted-foreground">/ 1,000</span>
                <Badge variant="outline" className={`ml-2 capitalize ${tier.badge}`}>{tier.name}</Badge>
              </div>
            </div>
            {stake.rewards_frozen && (
              <Badge variant="destructive" className="text-xs" data-testid="rewards-frozen-badge">
                <ShieldAlert className="h-3 w-3 mr-1" /> Allocation Restricted
              </Badge>
            )}
          </div>
          {/* Tier bar */}
          <div className="flex h-5 rounded-full overflow-hidden bg-[hsl(var(--web3-surface-2))]/60" data-testid="tier-bar">
            {TIERS.slice().reverse().map((t) => {
              const width = ((t.max - t.min + 1) / 1001) * 100;
              return (
                <div key={t.name} className={`${t.color} relative group`} style={{ width: `${width}%` }}>
                  <div className="opacity-0 group-hover:opacity-100 absolute -top-9 left-1/2 -translate-x-1/2 bg-popover border rounded px-2 py-0.5 text-[10px] whitespace-nowrap z-10 pointer-events-none transition-opacity capitalize">
                    {t.name} ({t.min}-{t.max}) {t.slash > 0 ? `${t.slash}% slash` : ''}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Position indicator */}
          <div className="relative h-3 mt-1">
            <div className="absolute top-0 -translate-x-1/2 flex flex-col items-center" style={{ left: `${healthPct}%` }}>
              <div className="w-0 h-0 border-l-[5px] border-r-[5px] border-b-[6px] border-transparent border-b-foreground" />
            </div>
          </div>
          <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
            <span>0 (Unacceptable)</span>
            <span>1000 (Excellent)</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ─── Simulation Panel ───
const SimulationPanel = ({ stake, hotelId, onEvaluated }) => {
  const [simScore, setSimScore] = useState([stake?.current_score || 500]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const tier = getTier(simScore[0]);
  const slashPreview = tier.slash > 0 ? Math.floor((stake?.staked_amount || 0) * (tier.slash / 100)) : 0;

  const handleEvaluate = async () => {
    setLoading(true);
    try {
      const r = await evaluateStaking(hotelId, simScore[0]);
      setResult(r);
      onEvaluated();
      toast.success(`Evaluation complete: Tier "${r.tier}"`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Evaluation failed');
    } finally { setLoading(false); }
  };

  return (
    <Card data-testid="simulation-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg heading-font flex items-center gap-2">
          <Zap className="h-5 w-5 text-[hsl(var(--web3-primary))]" />
          Score Simulator
        </CardTitle>
        <CardDescription>Preview what happens at different score levels before applying</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-xs uppercase tracking-widest">Simulated Score</Label>
            <span className={`text-2xl heading-font font-bold tabular-nums ${tier.text}`}>{simScore[0]}</span>
          </div>
          <Slider
            value={simScore}
            onValueChange={setSimScore}
            min={0} max={1000} step={10}
            className="[&>span>span]:bg-[hsl(var(--web3-primary))]"
            data-testid="score-slider"
          />
          <div className="flex justify-between text-[10px] text-muted-foreground">
            <span>0</span><span>300</span><span>500</span><span>700</span><span>800</span><span>900</span><span>1000</span>
          </div>
        </div>

        {/* Preview */}
        <div className="p-4 rounded-lg bg-[hsl(var(--web3-surface-2))]/60 space-y-3" data-testid="sim-preview">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Tier</span>
            <Badge variant="outline" className={`capitalize ${tier.badge}`}>{tier.name}</Badge>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Slash</span>
            {tier.slash > 0 ? (
              <span className="text-sm font-medium text-red-500">{tier.slash}% = {fmt(slashPreview)} HERMES</span>
            ) : (
              <span className="text-sm text-emerald-500">None</span>
            )}
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Allocation Status</span>
            <span className={`text-sm font-medium capitalize ${tier.reward === 'restricted' ? 'text-amber-500' : 'text-emerald-500'}`}>{tier.reward}</span>
          </div>
          {slashPreview > 0 && (
            <div className="text-xs text-red-400 bg-red-500/10 px-3 py-2 rounded-md flex items-start gap-2">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>Applying this score will slash {fmt(slashPreview)} HERMES ({tier.slash}% of reputation stake). 50% goes to protocol pool, 50% to whistleblower pool.</span>
            </div>
          )}
        </div>

        <Button onClick={handleEvaluate} disabled={loading} className="w-full" data-testid="evaluate-btn">
          {loading ? 'Evaluating...' : 'Apply Score & Evaluate'}
        </Button>

        {result && (
          <div className="p-3 rounded-lg border bg-card space-y-2" data-testid="eval-result">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              <span className="text-sm font-medium">Evaluation Applied</span>
            </div>
            <div className="text-xs text-muted-foreground space-y-1">
              <p>Score: {result.score} | Tier: <span className="capitalize">{result.tier}</span></p>
              {result.actions?.map((a, i) => (
                <p key={i} className={a.action === 'slashed' ? 'text-red-400' : 'text-amber-400'}>
                  {a.action === 'slashed' ? `Slashed: ${fmt(a.amount)} HERMES` : `Rewards: ${a.action}`}
                </p>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Event History ───
const EventHistory = ({ slashes, rewards }) => (
  <Card data-testid="event-history-card">
    <CardHeader className="pb-3">
      <CardTitle className="text-lg heading-font flex items-center gap-2">
        <Shield className="h-5 w-5 text-[hsl(var(--web3-primary))]" />
        Event History
      </CardTitle>
    </CardHeader>
    <CardContent>
      <ScrollArea className="h-[320px]">
        {slashes.length === 0 && rewards.length === 0 ? (
          <div className="text-center py-12">
            <Shield className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No reputation stake events yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {/* Merge and sort by timestamp */}
            {[
              ...slashes.map(s => ({ ...s, type: 'slash' })),
              ...rewards.map(r => ({ ...r, type: 'reward' })),
            ].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
              .map((event, i) => (
                <div key={i} className="flex items-center justify-between py-2.5 px-2 rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`h-7 w-7 rounded-full flex items-center justify-center shrink-0 ${
                      event.type === 'slash' ? 'bg-red-500/10' : 'bg-emerald-500/10'
                    }`}>
                      {event.type === 'slash'
                        ? <TrendingDown className="h-3.5 w-3.5 text-red-500" />
                        : <TrendingUp className="h-3.5 w-3.5 text-emerald-500" />}
                    </div>
                    <div>
                      <p className="text-sm font-medium capitalize">{event.type === 'slash' ? 'Slash' : 'Allocation'}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {event.type === 'slash'
                          ? `Score: ${event.score_at_slash} | Tier: ${event.tier_at_slash}`
                          : `Tier: ${event.tier} | Score: ${event.score}`}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm heading-font tabular-nums font-medium ${
                      event.type === 'slash' ? 'text-red-500' : 'text-emerald-500'
                    }`}>
                      {event.type === 'slash' ? '-' : '+'}{fmt(event.amount)}
                    </p>
                    <p className="text-[10px] text-muted-foreground tabular-nums">
                      {new Date(event.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
          </div>
        )}
      </ScrollArea>
    </CardContent>
  </Card>
);

// ─── Tier Reference ───
const TierReference = () => (
  <Card data-testid="tier-reference-card">
    <CardHeader className="pb-3">
      <CardTitle className="text-lg heading-font flex items-center gap-2">
        <Gauge className="h-5 w-5 text-[hsl(var(--web3-primary))]" />
        Tier Reference
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="space-y-1">
        {TIERS.map((t) => (
          <div key={t.name} className="flex items-center justify-between py-2 px-2 rounded-lg hover:bg-muted/50">
            <div className="flex items-center gap-3">
              <div className={`h-3 w-3 rounded-full ${t.color}`} />
              <div>
                <span className="text-sm font-medium capitalize">{t.name}</span>
                <span className="text-xs text-muted-foreground ml-2">({t.min}—{t.max})</span>
              </div>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <span className={t.slash > 0 ? 'text-red-400' : 'text-muted-foreground'}>
                {t.slash > 0 ? `${t.slash}% slash` : 'No slash'}
              </span>
              <Badge variant="outline" className={`text-[10px] capitalize ${
                t.reward === 'restricted' ? 'border-amber-500/30 text-amber-500' : 'border-emerald-500/30 text-emerald-500'
              }`}>
                {t.reward}
              </Badge>
            </div>
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

// ─── Main Page ───
export default function StakingDashboardPage() {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [hotelId, setHotelId] = useState('');
  const [stake, setStake] = useState(null);
  const [slashes, setSlashes] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const [s, sl, rw] = await Promise.all([
        getStakeStatus(id),
        getSlashHistory(id),
        getRewardHistory(id),
      ]);
      if (s.error) { toast.error(s.error); setLoading(false); return; }
      setStake(s);
      setSlashes(sl.events || []);
      setRewards(rw.events || []);
      setHotelId(id);
    } catch { toast.error('Failed to load staking data'); }
    finally { setLoading(false); }
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchInput.trim()) loadData(searchInput.trim());
  };

  return (
    <div className="terminal-theme min-h-dvh">
      {/* Header */}
      <div className="sticky top-0 z-30 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-button">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl md:text-2xl heading-font font-semibold tracking-tight" data-testid="page-title">Reputation Stake</h1>
              <p className="text-xs text-muted-foreground">Quality Enforcement & Reputation Management</p>
            </div>
          </div>
          {hotelId && (
            <Button variant="outline" size="sm" onClick={() => loadData(hotelId)} data-testid="refresh-btn">
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-6 space-y-6">
        <form onSubmit={handleSearch} className="flex gap-2 max-w-md" data-testid="hotel-search-form">
          <Input
            placeholder="Enter Hotel ID (e.g. sunrise-beach-resort-ff14)"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="text-sm"
            data-testid="hotel-id-input"
          />
          <Button type="submit" size="sm" variant="outline" data-testid="search-btn">
            <Search className="h-4 w-4" />
          </Button>
        </form>

        {loading ? (
          <div className="space-y-5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[1,2,3,4].map(i => <Card key={i}><CardContent className="pt-5 pb-4 space-y-3"><Skeleton className="h-3 w-24" /><Skeleton className="h-8 w-32" /></CardContent></Card>)}
            </div>
          </div>
        ) : !stake ? (
          <div className="flex flex-col items-center justify-center py-20 text-center space-y-3">
            <Lock className="h-12 w-12 text-muted-foreground/40" />
            <p className="text-lg heading-font text-muted-foreground">Enter a Hotel ID to view reputation stake status</p>
            <p className="text-sm text-muted-foreground max-w-md">
              Hotels commit HERMES tokens as a reputation stake. Bad behavior triggers slashing; good behavior qualifies for token allocations.
            </p>
          </div>
        ) : (
          <>
            <StakeOverview stake={stake} />
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
              <div className="lg:col-span-5 space-y-5">
                <SimulationPanel stake={stake} hotelId={hotelId} onEvaluated={() => loadData(hotelId)} />
                <TierReference />
              </div>
              <div className="lg:col-span-7">
                <EventHistory slashes={slashes} rewards={rewards} />
              </div>
            </div>
          </>
        )}
      </div>
      <TerminalFloorNav />
    </div>
  );
}
